import clevertap from 'clevertap-web-sdk';
import { CleverTapLogLevel, CleverTapProfile, Region } from 'types/clevertap';

let isInitialized = false;
let lastIdentity: string | null = null;

function parseBooleanEnv(value: unknown, fallback = false): boolean {
  if (typeof value !== 'string') {
    return fallback;
  }

  return value.toLowerCase() === 'true';
}

function buildPhoneNumber(profile: CleverTapProfile): string | undefined {
  if (!profile?.phone) {
    return undefined;
  }

  if (profile.phone.startsWith('+')) {
    return profile.phone;
  }

  if (profile.country_phone_code) {
    return `${profile.country_phone_code}${profile.phone}`;
  }

  return undefined;
}

export function initializeCleverTap(): boolean {
  if (isInitialized) {
    return true;
  }

  const accountId = process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID;
  const region = process.env.REACT_APP_CLEVERTAP_REGION as Region | undefined;
  const targetDomain = process.env.REACT_APP_CLEVERTAP_TARGET_DOMAIN?.trim();

  if (!accountId) {
    return false;
  }

  clevertap.privacy.push({
    optOut: parseBooleanEnv(process.env.REACT_APP_CLEVERTAP_OPTOUT),
  });
  clevertap.privacy.push({
    useIP: parseBooleanEnv(process.env.REACT_APP_CLEVERTAP_USE_IP),
  });

  clevertap.spa = true;

  if (targetDomain) {
    clevertap.init(accountId, region, targetDomain);
  } else {
    clevertap.init(accountId, region);
  }

  const logLevel = Number(process.env.REACT_APP_CLEVERTAP_LOG_LEVEL ?? '0');
  if (!Number.isNaN(logLevel) && logLevel > 0) {
    clevertap.setLogLevel(logLevel as CleverTapLogLevel);
  }

  globalThis.clevertap = clevertap;

  isInitialized = true;
  return true;
}

export function syncCleverTapUser(profile: CleverTapProfile): void {
  if (!profile || !initializeCleverTap()) {
    return;
  }

  const identity = String(
    profile.user_id ??
      profile.corporate_id ??
      profile?.user_logged?.email ??
      '',
  ).trim();
  const payload = {
    Site: {
      Name: profile.name,
      Identity: identity || undefined,
      Email: profile?.user_logged?.email ?? profile.email,
      Phone: buildPhoneNumber(profile),
    },
  };

  if (identity && identity !== lastIdentity) {
    clevertap.onUserLogin.push(payload);
    lastIdentity = identity;
    return;
  }

  clevertap.profile.push(payload);
}

export function resetCleverTapIdentity(): void {
  lastIdentity = null;
}

// In SPA mode, CleverTap doesn't automatically re-evaluate campaigns when
// the user identity changes. pageChanged() signals a new page view with the
// now-identified user, triggering campaign re-delivery for active slots.
export function notifyCleverTapPageChanged(): void {
  globalThis.clevertap?.pageChanged();
}
